import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DurationInterface, Order, SalesOrdersService } from '@vendasta/sales-orders';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { DurationMapping } from '../order-detail-field';
import { ContractDurationKeysToEnum, ContractDurationTranslationKey } from './sales-contract-duration';

const MIN_CONTRACT_DURATION_REGEX = '^[1-9][0-9]*?$'; // regex for checking minimum valid contract duration (1 year or month)

@Component({
  selector: 'sales-ui-sales-order-details-contract-duration',
  templateUrl: './sales-order-details-contract-duration.component.html',
  styleUrls: ['./sales-order-details-contract-duration.component.scss', '../sales-order-details-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesOrderDetailsContractDurationComponent implements OnInit, OnChanges {
  @Input() order: Order;
  @Input() editable = false;

  isEditing = false;
  contractDuration: DurationInterface;
  contractDurationForm: UntypedFormGroup;
  contractPeriods: ContractDurationTranslationKey[] = ContractDurationKeysToEnum;
  updating = false;

  contractDurationText$: Observable<string>;

  constructor(
    private readonly salesOrdersService: SalesOrdersService,
    private readonly alertService: SnackbarService,
    private readonly translateService: TranslateService,
    private cdk: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.editable = changes.editable ? changes.editable.currentValue : this.editable;
    this.contractDuration = changes.contractDuration ? changes.contractDuration.currentValue : this.contractDuration;
    this.contractDuration = this.order.contractDuration || {};
  }

  ngOnInit(): void {
    this.contractDuration = this.order.contractDuration || {};
    this.contractDurationForm = new UntypedFormGroup({
      value: new UntypedFormControl(this.contractDuration && this.contractDuration.value, [
        Validators.pattern(MIN_CONTRACT_DURATION_REGEX),
      ]),
      duration: new UntypedFormControl(this.contractDuration && this.contractDuration.duration, [Validators.required]),
    });

    this.convertDuration();
  }

  convertDuration(): void {
    this.contractDuration = this.contractDuration || {};

    if (!this.contractDuration.value) {
      this.contractDurationText$ = this.translateService.stream('FRONTEND.SALES_UI.NO_CONTRACT_LENGTH');
    } else {
      this.contractDurationText$ = this.translateService.stream(DurationMapping.get(this.contractDuration.duration), {
        duration: this.contractDuration.value.toString(),
      });
    }
  }

  invalidDurationValue(): boolean {
    this.contractDurationForm.markAsTouched();
    return this.contractDurationForm.controls.value.invalid && this.contractDurationForm.controls.value.dirty;
  }

  invalidDurationPeriod(): boolean {
    this.contractDurationForm.controls.duration.markAsTouched();
    return this.contractDurationForm.controls.duration.invalid;
  }

  edit(): void {
    this.isEditing = true;
  }

  updateContractDuration(): void {
    if (this.invalidDurationValue()) {
      return;
    }

    if (this.invalidDurationPeriod()) {
      return;
    }

    // if field is empty and user hits send, reset length
    if (this.contractDuration && !this.contractDuration.value) {
      this.contractDuration = null;
    }

    this.isEditing = true;
    this.updating = true;
    this.salesOrdersService
      .updateContractDuration(this.order.orderId, this.order.businessId, this.contractDuration)
      .pipe(take(1))
      .subscribe(
        () => {
          this.isEditing = false;
          this.updating = false;
          this.convertDuration();
          this.cdk.detectChanges();
        },
        () => {
          this.isEditing = false;
          this.updating = false;
          this.alertService.openErrorSnack('FRONTEND.SALES_UI.CONTRACT_LENGTH_ERROR');
          this.convertDuration();
          this.cdk.detectChanges();
        },
      );
  }
}
